import { supabase, TABLES, COLUMN_MAPS } from '../supabase';
import { Project } from '../../types';
import { PAGINATION } from '../../utils/constants';

// ============================================
// Projects Service
// ============================================

export const projectsApi = {
  /**
   * Get all projects
   */
  async getAll(): Promise<Project[]> {
    const { data, error } = await supabase
      .from(TABLES.PROJECTS)
      .select('*');
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.project.fromDb);
  },

  /**
   * Get projects with pagination
   */
  async getPaginated(
    page = PAGINATION.DEFAULT_PAGE, 
    limit = PAGINATION.DEFAULT_LIMIT
  ): Promise<{ data: Project[]; total: number }> {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.PROJECTS)
      .select('*', { count: 'exact' })
      .range(from, to)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return {
      data: (data || []).map(COLUMN_MAPS.project.fromDb),
      total: count || 0,
    };
  },

  /**
   * Get a single project by ID
   */
  async getById(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from(TABLES.PROJECTS)
      .select('*')
      .eq('id', id)
      .maybeSingle();
    
    if (error) throw error;
    return data ? COLUMN_MAPS.project.fromDb(data) : null;
  },

  /**
   * Get projects by user membership
   */
  async getByMember(userId: string): Promise<Project[]> {
    const { data, error } = await supabase
      .from(TABLES.PROJECTS)
      .select('*')
      .contains('member_ids', [userId]);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.project.fromDb);
  },

  /**
   * Get projects by owner
   */
  async getByOwner(ownerId: string): Promise<Project[]> {
    const { data, error } = await supabase
      .from(TABLES.PROJECTS)
      .select('*')
      .eq('owner_id', ownerId);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.project.fromDb);
  },

  /**
   * Get active projects only
   */
  async getActive(): Promise<Project[]> {
    const { data, error } = await supabase
      .from(TABLES.PROJECTS)
      .select('*')
      .eq('status', 'active');
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.project.fromDb);
  },

  /**
   * Create a new project
   */
  async create(project: Project): Promise<void> {
    const dbData = COLUMN_MAPS.project.toDb(project);
    
    const { error } = await supabase
      .from(TABLES.PROJECTS)
      .insert(dbData);
    
    if (error) throw error;
  },

  /**
   * Update a project
   */
  async update(id: string, updates: Partial<Project>): Promise<void> {
    const dbUpdates: Record<string, any> = {};
    
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId;
    if (updates.memberIds !== undefined) dbUpdates.member_ids = updates.memberIds;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.deadline !== undefined) dbUpdates.deadline = updates.deadline;

    const { error } = await supabase
      .from(TABLES.PROJECTS)
      .update(dbUpdates)
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Delete a project
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.PROJECTS)
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Archive a project
   */
  async archive(id: string): Promise<void> {
    await this.update(id, { status: 'archived' });
  },

  /**
   * Restore an archived project
   */
  async restore(id: string): Promise<void> {
    await this.update(id, { status: 'active' });
  },

  /**
   * Add a member to a project
   */
  async addMember(projectId: string, userId: string): Promise<void> {
    const project = await this.getById(projectId);
    if (!project) throw new Error('Project not found');
    
    if (!project.memberIds.includes(userId)) {
      await this.update(projectId, {
        memberIds: [...project.memberIds, userId],
      });
    }
  },

  /**
   * Remove a member from a project
   */
  async removeMember(projectId: string, userId: string): Promise<void> {
    const project = await this.getById(projectId);
    if (!project) throw new Error('Project not found');
    
    await this.update(projectId, {
      memberIds: project.memberIds.filter(id => id !== userId),
    });
  },
};

export default projectsApi;
